import * as React from "react"
import { ExternalLink, Loader2, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useBookingT } from "@/hooks/use-translations"
import { HrisApiError, type RealtimeOAuthClient } from "@/features/integrations/hris/hrisApi"

type Props = {
  tenantUrl: string
  busy: boolean
  /** Resolves true when real-time updates came on. */
  onSubmit: (client: RealtimeOAuthClient) => Promise<boolean>
  onCancel: () => void
}

/**
 * Lucca manages webhooks only through an OAuth client, not the API key the
 * sync uses, so real-time updates ask for one: where to create it, which
 * scopes, then its ID and secret.
 */
export function HrisLuccaRealtimeForm({ tenantUrl, busy, onSubmit, onCancel }: Props) {
  const t = useBookingT()
  const [clientId, setClientId] = React.useState("")
  const [secret, setSecret] = React.useState("")
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const keysUrl = `${tenantUrl.replace(/\/+$/, "")}/identity/admin/integration-keys`

  const scopes = [
    t("hris_rt_scope_webhooks", "Webhook endpoints (read and write)"),
    t("hris_scope_employees", "Employees"),
    t("hris_scope_employments", "Employments"),
    t("hris_scope_positions", "Job positions"),
    t("hris_rt_scope_attributes", "Employee attributes"),
  ]

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    try {
      await onSubmit({ oauthClientId: clientId.trim(), oauthClientSecret: secret.trim() })
    } catch (err) {
      const fields = err instanceof HrisApiError ? err.fields : {}
      setErrors({
        oauthClientId: fields.oauthClientId?.[0] ?? "",
        oauthClientSecret: fields.oauthClientSecret?.[0] ?? (Object.keys(fields).length === 0 ? (err as Error).message : ""),
      })
    }
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-4 rounded-md border border-neutral-200 bg-neutral-50 p-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-neutral-900">
          {t("hris_rt_title", "Lucca sends changes to Travel Code only through an OAuth client")}
        </p>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-neutral-800">
          <li>
            {t("hris_rt_step1", "In Lucca, open Administration → Integration keys and create an OAuth client named “Travel Code real-time updates”.")}{" "}
            <a href={keysUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-600 underline">
              {t("hris_guide_open_keys", "Open it in your Lucca account")}
              <ExternalLink className="size-3.5" />
            </a>
          </li>
          <li>
            {t("hris_rt_step2", "Give it these scopes:")}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {scopes.map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
          </li>
          <li>{t("hris_rt_step3", "Paste its client ID and secret here.")}</li>
        </ol>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="hris-rt-client">{t("hris_rt_client_id", "Client ID")}</Label>
          <Input
            id="hris-rt-client"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
            className={errors.oauthClientId ? "border-error-500 bg-white focus-visible:ring-error-500" : "bg-white"}
          />
          {errors.oauthClientId && <p className="text-sm text-error-700">{errors.oauthClientId}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="hris-rt-secret">{t("hris_rt_client_secret", "Client secret")}</Label>
          <Input
            id="hris-rt-secret"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="new-password"
            className={errors.oauthClientSecret ? "border-error-500 bg-white focus-visible:ring-error-500" : "bg-white"}
          />
          {errors.oauthClientSecret && <p className="text-sm text-error-700">{errors.oauthClientSecret}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 max-md:flex-col max-md:items-stretch">
        <p className="flex items-center gap-1.5 text-sm text-neutral-800">
          <ShieldCheck className="size-4 text-success-600" />
          {t("hris_rt_safety", "Stored encrypted, used only to manage the Travel Code webhook.")}
        </p>
        <div className="flex gap-2 max-md:flex-col">
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            {t("hris_cancel", "Cancel")}
          </Button>
          <Button type="submit" disabled={busy || !clientId.trim() || !secret.trim()}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t("hris_updates_enable", "Turn on real-time updates")}
          </Button>
        </div>
      </div>
    </form>
  )
}
